import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { ExpressChip, LineDot, Loading, StatusPill, useNow, useTitle } from '../../ui/bits';
import { Scene, SceneError } from '../../catalog/art';
import { Icon } from '../../ui/Icon';
import { Stat } from '../../ui/viz';
import { usePrefs } from '../../ui/prefs';
import { useToast } from '../../ui/toast';
import { playNewOrderTone } from '../../ui/sound';
import { clock, count, updatedAgo } from '../../lib/format.js';
import { useShop } from '../ShopLayout';
import { HANDOFF_MS, STATE_VIEW, STATION_NAME, ageText, arrivalMessage, bagText, boardCounts, cardTitle, detectArrivals, riderView, sortStations, splitStuck, stationText, stuckTitle, type HandoffItem } from './lib/handoff.js';
import { handoffKeys } from './keys';
import './handoff.css';

type Seen = { ready: Set<string>; here: Set<string> };

const sentence = (t: string) => (t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : t);

/** /shop/:shopId/handoff — Ready for rider: every order's stations on one card, so riders take whole orders. */
export function ShopHandoffPage() {
  useTitle('Ready for rider');
  const { shopId, queue } = useShop();
  const qc = useQueryClient();
  const toast = useToast();
  const { muted } = usePrefs();
  const now = useNow(5_000);

  const board = useQuery({
    queryKey: handoffKeys.board(shopId),
    queryFn: () => api.shopHandoff(shopId),
    refetchInterval: HANDOFF_MS,
    refetchIntervalInBackground: true,
    enabled: !!shopId,
  });
  // Stuck orders (older than the Ops limit) fold into their own group under the live board.
  const { live, stuck } = useMemo(() => splitStuck(board.data ?? []), [board.data]);
  const counts = boardCounts(live);

  // The order's live events: the layout polls the queue every 5 s; when any order there moves
  // (a new one, a status change), refetch the board at once instead of waiting for the 10 s tick.
  const queueSig = queue.map((q) => `${q.shopOrderId}:${q.status}`).join('|');
  const lastSig = useRef(queueSig);
  useEffect(() => {
    if (lastSig.current === queueSig) return;
    lastSig.current = queueSig;
    void qc.invalidateQueries({ queryKey: handoffKeys.board(shopId) });
  }, [queueSig, qc, shopId]);

  // A rider at the counter (or an order going all ready) chimes once, unless Sound is off.
  const seen = useRef<Seen | null>(null);
  const [flash, setFlash] = useState<Set<string>>(() => new Set());
  const [announce, setAnnounce] = useState('');
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  useEffect(() => {
    seen.current = null;
    setFlash(new Set());
  }, [shopId]);
  useEffect(() => {
    if (!board.data) return;
    const { arrived, nowReady, seen: next } = detectArrivals(seen.current, board.data);
    seen.current = next;
    const msg = arrivalMessage(arrived, nowReady);
    if (!msg) return;
    if (arrived.length && !mutedRef.current) playNewOrderTone();
    if (arrived.length) toast(msg, 'order');
    setAnnounce(msg);
    setFlash((s) => {
      const n = new Set(s);
      for (const o of [...arrived, ...nowReady]) n.add(o.orderId);
      return n;
    });
  }, [board.data, toast]);

  return (
    <>
      <header className="dv-head">
        <div className="stack s4">
          <h1>Ready for rider</h1>
          <span className="dv-live" title="Express first. Riders take whole orders.">
            <span className="dv-pulse" aria-hidden="true" />
            <span aria-hidden="true">{sentence(updatedAgo(board.dataUpdatedAt, now))}</span>
          </span>
        </div>
        <div className="dv-actions">
          <SoundToggle />
        </div>
      </header>
      {board.data && (live.length || stuck.length) ? (
        <div className="dv-stats ho-stats dv-rise">
          <Stat label="Waiting" value={count(counts.total)} visual={<ReadyBar total={counts.total} ready={counts.ready} />} />
          <Stat tone="ink" label="All ready" value={count(counts.ready)} sub={counts.ready ? 'Bag it, hand it over' : undefined} />
          <Stat label="At the counter" value={count(counts.atCounter)} visual={<span className="dv-disc" aria-hidden="true"><Icon name="person" /></span>} />
          {stuck.length ? <Stat label="Stuck" value={count(stuck.length)} tone="alert" /> : null}
        </div>
      ) : null}
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announce}
      </p>

      {board.isError && !board.data ? (
        <section className="card">
          <SceneError error={board.error} onRetry={() => void board.refetch()} />
        </section>
      ) : board.isLoading ? (
        <Loading label="Loading the handoff board" rows={3} />
      ) : live.length === 0 && stuck.length === 0 ? (
        <section className="card">
          <Scene scene="delivered" title="Nothing waiting for a rider">
            Orders show up here once they're in the shop. When every station is done, the card turns yellow: bag it and hand it over.
          </Scene>
        </section>
      ) : (
        <div className="stack s24">
          {live.length ? (
            <ul className="ho-board" aria-label="Orders waiting for a rider">
              {live.map((o) => (
                <li key={o.orderId}>
                  <HandoffCard o={o} isNew={flash.has(o.orderId)} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="dv-quiet ho-live-empty">
              <Icon name="check" />
              Nothing live right now. Older orders are under Stuck.
            </p>
          )}
          {stuck.length ? <StuckGroup orders={stuck} flash={flash} /> : null}
        </div>
      )}
    </>
  );
}

function SoundToggle() {
  const { muted, setMuted } = usePrefs();
  return (
    <button type="button" className="dv-ibtn press" aria-pressed={!muted} aria-label={`Sound ${muted ? 'off' : 'on'}`} title={`Rider sound ${muted ? 'off' : 'on'}`} onClick={() => setMuted(!muted)}>
      <Icon name={muted ? 'bellOff' : 'bell'} />
    </button>
  );
}

/** Folded by default: orders past the Ops limit stay visible without crowding the live board. */
function StuckGroup({ orders, flash }: { orders: HandoffItem[]; flash: Set<string> }) {
  return (
    <details className="card panel ho-stuck">
      <summary className="ho-stuck-summary">
        <h2>{stuckTitle(orders.length)}</h2>
        <span className="dv-sub" title="Older orders, folded away so the live board stays clear.">
          Hand them over, or ask Ops
        </span>
      </summary>
      <ul className="ho-board" aria-label="Stuck orders">
        {orders.map((o) => (
          <li key={o.orderId}>
            <HandoffCard o={o} isNew={flash.has(o.orderId)} />
          </li>
        ))}
      </ul>
    </details>
  );
}

/** Share of waiting orders that are all ready, as a short bar (the Waiting tile's visual). */
function ReadyBar({ total, ready }: { total: number; ready: number }) {
  return (
    <span className="ho-readybar" aria-hidden="true">
      <span className="dv-share">
        <i style={{ width: `${total ? (ready / total) * 100 : 0}%` }} />
      </span>
    </span>
  );
}

function HandoffCard({ o, isNew }: { o: HandoffItem; isNew: boolean }) {
  const rider = riderView(o.rider);
  const stuck = o.stuck === true;
  const age = stuck ? ageText(o.ageMinutes) : null;
  const cls = ['ticket', 'ho-card', o.allReady ? 'ready' : '', stuck ? 'stuck' : '', isNew ? 'new' : ''].filter(Boolean).join(' ');
  return (
    <article className={cls} aria-label={`${o.code}${o.allReady ? ', all ready' : ''}${stuck ? `, stuck${age ? ` ${age}` : ''}` : ''}`} data-order={o.orderId}>
      {o.speed === 'express' || o.allReady || stuck ? (
        <div className="meta">
          {stuck ? <StatusPill tone="warning">Stuck</StatusPill> : null}
          {stuck && age ? <span className="t-label ho-age">Waiting {age}</span> : null}
          {o.speed === 'express' ? <ExpressChip /> : null}
          {o.allReady ? (
            <span className="pill sticker ho-ready-sticker">
              <Icon name="check" size="sm" />
              All ready
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="ho-head">
        <h2 className="id ho-code">{o.code}</h2>
        <span className="ho-time" title={o.readyBy ? 'Ready by' : 'Placed'}>
          <Icon name="clock" size="sm" />
          <span className="sr-only">{o.readyBy ? 'Ready by ' : 'Placed '}</span>
          {clock(o.readyBy ?? o.createdAt)}
        </span>
      </div>
      <span className="ho-title">{cardTitle(o)}</span>
      <span className="ho-prog" aria-hidden="true">
        {sortStations(o.stations).map((s) => (
          <i key={s.line} className={`is-${s.state}`} />
        ))}
      </span>

      <ul className="ho-stations" aria-label={`${o.code} stations`}>
        {sortStations(o.stations).map((s) => {
          const st = STATE_VIEW[s.state];
          return (
            <li key={s.line} className={`ho-station is-${s.state}`}>
              <LineDot line={s.line} />
              <span className="grow stack tight">
                <b className="ho-st-nm">{STATION_NAME[s.line]}</b>
                <span className="ho-st-tx">{stationText(s)}</span>
              </span>
              <StatusPill tone={st.tone}>{st.label}</StatusPill>
            </li>
          );
        })}
      </ul>

      <div className="row between wrap ho-foot">
        <span className="row ho-bags">
          <Icon name="bag" size="sm" />
          <span>{bagText(o.bagCount)}</span>
        </span>
        <span className={`row ho-rider${rider.here ? ' here' : ''}`}>
          <Icon name="person" size="sm" />
          <span>{rider.text}</span>
        </span>
      </div>
    </article>
  );
}
